import {
    Grid,
    IconButton,
    InternalStandardProps,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
    VideoDexie,
    AudioDexie,
    getDexieConnectionRefresh,
} from "JS/Database/Dexie";
import { MediaPlayer } from "JS/React/Components/MediaPlayer";
import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { AppConfimationDialog } from "JS/React/Components/AppConfirmationDialog";
import { AppHeader } from "JS/React/Components/AppHeader";
import { useSnackbar } from "JS/Helpers/Snackbar";
import { messaging } from "JS/Helpers/UserMessaging";
import FallbackWaveform from "Images/Content/audio-waves.png";
import {
    EventActions,
    EventCategories,
    EventNames,
    FirebaseEventAction,
    ReceivedGift,
    contentTypeOf,
} from "JS/Models";
import moment from "moment";
import {
    useGlobalGifts,
    useReceivedMemberAudioGifts,
    useReceivedMemberVideoGifts,
} from "JS/React/Hooks/Gifts";
import { getDownloadedPartsNids } from "JS/Helpers";
import { useFirebaseLogger } from "JS/React/Hooks/Firebase";
import { useOwnedBlobUrl } from "JS/React/Hooks/Media";
import { AppButton } from "JS/React/Components/AppButton";
import { useRouting } from "JS/React/Hooks/Routes";
import {
    useContentPlayingHandlers,
    usePlayerHandlers,
} from "JS/React/Hooks/MediaPlayer";
import { PlayerActionBtns } from "JS/React/Components/MediaPlayer/PlayerActionBtns";
import {
    useIsResumingAudio,
    useIsResumingVideo,
    useResumeRecentlyOfflineContent,
} from "JS/React/Hooks/ResumeRecently";
import {
    usePublishDisclosurePlaying,
    useDisclosureGate,
} from "JS/React/Hooks/Disclosure";
import { useDisclosureSequence } from "JS/React/Hooks/DisclosureSequence";
import { DisclosureBadge } from "JS/React/Components/MediaPlayer/DisclosureBadge";
import { PLAYER_PROGRESS_INTERVAL } from "JS/Helpers/Constants";
import { AppDownloadingIcons } from "JS/React/Components/Icon/AppDownloadingIcons";
import {
    SentGiftType,
    getPlayerPlayedTimeToLog,
    toSentOfflineGift,
} from "JS/Models/Firebase/GiftDashboard";
import qs from "qs";
import { useGiftDashboard } from "JS/React/Hooks/Firebase/GiftDashboard";
import { useGlobalAudioDetails } from "JS/React/Hooks/Audio";
import { useGlobalVideoDetails } from "JS/React/Hooks/Video";

const RootDiv = styled("div")({});

const Grey500Typography = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[500],
}));

const Grey800Typography = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey[800],
}));

const GreyA100Typography = styled(Typography)(({ theme }) => ({
    color: theme.palette.grey.A100,
}));

const DateTypography = styled(Grey500Typography)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
}));

const PlayButtonWrapper = styled("div")({
    display: "flex",
    justifyContent: "center",
});

const PlayerContainerGrid = styled(Grid)(({ theme }) => ({
    margin: theme.spacing(2, 0),
    height: "60vh",
    [theme.breakpoints.down("lg")]: {
        height: "50vh",
    },
    [theme.breakpoints.down("sm")]: {
        height: "30vh",
    },
}));

const BodyPaddingGrid = styled(Grid)({
    paddingRight: "10px",
    paddingLeft: "10px",
});

const BodyPaddingDiv = styled("div")({
    paddingRight: "10px",
    paddingLeft: "10px",
});

export interface ContentDetailOfflineProps
    extends InternalStandardProps<
        React.DetailedHTMLProps<
            React.HTMLAttributes<HTMLDivElement>,
            HTMLDivElement
        >
    > {}

export const ContentDetailOffline = (props: ContentDetailOfflineProps) => {
    const { className, ...rest } = props;
    const [content, setContent] = useState<VideoDexie | AudioDexie>();
    const params: any = useParams();
    const navigate = useNavigate();

    const { enqueueSnackbar } = useSnackbar();

    const { logFirebaseEvent } = useFirebaseLogger();

    const [isOpenConfirmDailog, setIsOpenConfirmDailog] = useState(false);
    const [deleteLoader, setDeleteLoader] = useState(false);

    const { linkProvider } = useRouting();

    const {
        receivedMemberAudios,
        receivedProspectAudios,
        receivedMemberVideos,
        receivedProspectVideos,
    } = useGlobalGifts();
    const { scrollTo } = useQueryParams();
    const { nid, type, isAutoPlay } = params;
    const [currentPlayingState, setCurrentPlayingState] = useState(
        isAutoPlay === "true",
    );
    const { handlePlayerPause, handlePlayerPlay } = useContentPlayingHandlers(
        setCurrentPlayingState,
    );

    const contentNid = nid.split("-")[1] ? nid.split("-")[1] : "0";

    const { description } = useDescription(type, contentNid);

    const isGift =
        type === "audio"
            ? receivedMemberAudios.find((r) => r.nid === contentNid) !==
                  undefined ||
              receivedProspectAudios.find((r) => r.nid === contentNid) !==
                  undefined
            : receivedMemberVideos.find((r) => r.nid === contentNid) !==
                  undefined ||
              receivedProspectVideos.find((r) => r.nid === contentNid) !==
                  undefined;

    const currentGift: ReceivedGift = useMemo(() => {
        if (type === "audio") {
            return (
                receivedMemberAudios.find((r) => r.nid === contentNid) ||
                receivedProspectAudios.find((r) => r.nid === contentNid)
            );
        } else {
            return (
                receivedMemberVideos.find((r) => r.nid === contentNid) ||
                receivedProspectVideos.find((r) => r.nid === contentNid)
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        receivedMemberAudios,
        receivedProspectAudios,
        receivedMemberVideos,
        receivedProspectVideos,
        type,
    ]);

    const {
        playerRef,
        handleContentCompleted,
        onMediaPlayerReady,
        onPlayerProgress,
        setIsReady,
        isReadyToPlay,
        setIsReadyToPlay,
    } = useResumeRecentlyOfflineContent(content, type);

    const { handleChangeSeek } = usePlayerHandlers(playerRef);

    const getDashboardActionDetails = () => {
        return getPlayerPlayedTimeToLog(playerRef);
    };

    const giftDashboardData = useMemo(() => {
        return toSentOfflineGift(content);
    }, [content]);

    const { isGift: isAudioGift } = useReceivedMemberAudioGifts(true);
    const { isGift: isVideoGift } = useReceivedMemberVideoGifts(true);
    const isReceivedMemberGift =
        isAudioGift(contentNid) || isVideoGift(contentNid);

    const { logGiftOpen, logGiftCompleted } = useGiftDashboard(
        giftDashboardData,
        type === "audio" ? SentGiftType.AUDIO : SentGiftType.VIDEO,
        currentGift?.sender?.member_id,
        isReceivedMemberGift && currentPlayingState,
        getDashboardActionDetails,
        true,
    );

    const playerEnded = () => {
        handleContentCompleted();
        if (isReceivedMemberGift) logGiftCompleted();
    };

    useEffect(() => {
        (async () => {
            const db = getDexieConnectionRefresh();
            const activeDb = type === "audio" ? db.audios : db.videos;

            const response = await activeDb.get(nid);

            setContent({
                ...response,
                description: description || response.description,
            });
        })();
    }, [nid, type, description]);

    useEffect(() => {
        if (content) {
            let category = EventCategories.VIDEOS;
            let name = EventNames.VIDEO_OPENED;
            if (isGift) {
                category = EventCategories.GIFT_VIDEOS;
                name = EventNames.GIFT_VIDEO_OPENED;
            }
            if (type === "audio") {
                category = EventCategories.AUDIOS;
                name = EventNames.AUDIO_OPENED;
                if (isGift) {
                    category = EventCategories.GIFT_AUDIOS;
                    name = EventNames.GIFT_AUDIO_OPENED;
                }
            }
            const action: FirebaseEventAction = {
                action: EventActions.OPEN,
                contentTitle: content.name,
                nId: contentNid,
                skuId: content.skuId,
                category: category,
            };
            if (isReceivedMemberGift) logGiftOpen();
            logFirebaseEvent(name, action);
        }
    }, [type, content, nid, isReceivedMemberGift]);

    const handleToggleConfirmDialog = () => {
        setIsOpenConfirmDailog(!isOpenConfirmDailog);
    };

    const [isLoop, setLoop] = useState(false);

    const handleDelete = async (nid: string, type: string) => {
        if (type !== "audioBundle") {
            const db = getDexieConnectionRefresh();
            const activeDb = type === "audio" ? db.audios : db.videos;
            await activeDb
                .delete(nid)
                .then(() => {
                    enqueueSnackbar(messaging.download.deleteSuccess, {
                        variant: "success",
                    });
                    navigate(-1);
                })
                .catch(() => {
                    enqueueSnackbar(messaging.download.deleteError, {
                        variant: "error",
                    });
                });
        } else {
            const id = nid.split("-")[1];
            let deleteSuccess = true;
            setDeleteLoader(true);
            await getDownloadedPartsNids(id).then(async (res) => {
                if (res.length) {
                    for (let i = 0; i < res.length; i++) {
                        await getDexieConnectionRefresh()
                            .audios.delete(res[i])
                            .then(() => {})
                            // eslint-disable-next-line no-loop-func
                            .catch(() => {
                                deleteSuccess = false;
                            });
                        if (i === res.length - 1) {
                            if (deleteSuccess) {
                                enqueueSnackbar(
                                    messaging.download.deleteSuccess,
                                    {
                                        variant: "success",
                                    },
                                );
                                navigate(-1);
                            } else {
                                enqueueSnackbar(
                                    messaging.download.deleteError,
                                    {
                                        variant: "error",
                                    },
                                );
                            }
                            setDeleteLoader(false);
                        }
                    }
                }
            });
        }

        setIsOpenConfirmDailog(!isOpenConfirmDailog);
    };

    const handleAudioStartedPlaying = () => {
        if (content) {
            let category = EventCategories.VIDEOS;
            let name = EventNames.VIDEO_PLAYED;
            if (isGift) {
                category = EventCategories.GIFT_VIDEOS;
                name = EventNames.GIFT_VIDEO_PLAYED;
            }
            if (type === "audio") {
                category = EventCategories.AUDIOS;
                name = EventNames.AUDIO_PLAYED;
                if (isGift) {
                    category = EventCategories.GIFT_AUDIOS;
                    name = EventNames.GIFT_AUDIO_PLAYED;
                }
            }

            const action: FirebaseEventAction = {
                action: EventActions.PLAY,
                contentTitle: content.name,
                nId: contentNid,
                skuId: content.skuId,
                category: category,
            };
            logFirebaseEvent(name, action);
        }
    };

    const playClick = () => {
        navigate(linkProvider.react.offline().bundle().audios(content?.id));
    };

    const mediaURL = useMemo(() => {
        return content?.blob && content?.isBundle !== true
            ? URL.createObjectURL(content?.blob)
            : "";
    }, [content?.id]);

    useOwnedBlobUrl(mediaURL);

    const disclosure = useDisclosureGate({
        disclosure: content?.disclosure,
        skuId: content?.skuId,
        nId: contentNid,
        contentType: contentTypeOf(
            currentGift,
            type === "audio" ? "MP3" : "Video",
        ),
    });

    // the resume handler branches on type, so skipStart has to branch the same
    const isResumingAud = useIsResumingAudio(contentNid);
    const isResumingVid = useIsResumingVideo(contentNid);
    const isResuming = type === "audio" ? isResumingAud : isResumingVid;

    const sequence = useDisclosureSequence({
        contentUrl: mediaURL,
        loop: isLoop,
        disclosure: content?.disclosure,
        startUrl: disclosure.startUrl,
        endUrl: disclosure.endUrl,
        resolving: disclosure.resolving,
        autoPlay:
            type === "audio"
                ? isAutoPlay === "true" || currentPlayingState
                : isAutoPlay === "true" && currentPlayingState,
        skipStart: isResuming,
        analytics: disclosure.analytics,
    });

    usePublishDisclosurePlaying(sequence.isDisclosure);

    const handleSequencePlay = () => {
        sequence.onPlay();
        if (!sequence.isDisclosure) handlePlayerPlay();
    };

    const handleSequencePause = () => {
        sequence.onPause();
        if (!sequence.isDisclosure) handlePlayerPause();
    };

    const handleSequenceStarted = () => {
        if (!sequence.isDisclosure) handleAudioStartedPlaying();
    };

    const handleSequenceEnded = () => {
        const contentJustEnded = !sequence.isDisclosure;
        sequence.handleEnded();
        if (contentJustEnded) playerEnded();
    };

    return (
        <RootDiv {...rest} className={className}>
            <AppHeader
                title={content?.name}
                canGoBack={true}
                searchable={false}
                showTabs={false}
                onBackClick={() => {
                    navigate(
                        linkProvider.react
                            .offline()
                            .listing(type === "audio" ? "Audios" : "Videos", {
                                scrollTo: scrollTo ? +scrollTo : 0,
                            }),
                    );
                }}
            />
            <BodyPaddingGrid
                item
                xs={12}
                display="flex"
                justifyContent="space-between"
            >
                <GreyA100Typography fontWeight={"bold"} variant="h6">
                    {content?.name}
                </GreyA100Typography>
                <div>
                    <IconButton>
                        <AppDownloadingIcons
                            iconType="trash"
                            onClick={handleToggleConfirmDialog}
                        />
                    </IconButton>

                    {isOpenConfirmDailog && (
                        <AppConfimationDialog
                            open={isOpenConfirmDailog}
                            onConfirm={() =>
                                handleDelete(
                                    nid,
                                    content?.isBundle ? "audioBundle" : type,
                                )
                            }
                            btnConfirmLoader={deleteLoader}
                            onClose={handleToggleConfirmDialog}
                            popupMainText={`Are you sure you want to delete ${
                                content?.isBundle ? "Audio Bundle" : type
                            }?`}
                            confirmText="Delete"
                            cancelButtonText="Cancel"
                            showConfirmFirst={true}
                        />
                    )}
                </div>
            </BodyPaddingGrid>
            <BodyPaddingGrid item xs={12} display="flex">
                <Grey500Typography variant="body1">
                    {content?.skuId}
                </Grey500Typography>
            </BodyPaddingGrid>
            <BodyPaddingGrid item xs={12}>
                <DisclosureBadge
                    placement={sequence.step}
                    mediaNoun={type === "video" ? "video" : "audio"}
                />
            </BodyPaddingGrid>
            <PlayerContainerGrid
                item
                xs={12}
                sx={{ paddingRight: "10px", paddingLeft: "10px" }}
            >
                <MediaPlayer
                    title={content?.name}
                    mediaURL={sequence.url}
                    restartToken={sequence.stepToken}
                    imageURL={FallbackWaveform}
                    playerRef={playerRef}
                    progressInterval={PLAYER_PROGRESS_INTERVAL}
                    loop={sequence.isSequencing ? false : isLoop}
                    onStart={handleSequenceStarted}
                    onReady={() => onMediaPlayerReady()}
                    onProgress={onPlayerProgress}
                    onEnded={handleSequenceEnded}
                    isAudio={type === "audio"}
                    playing={sequence.playing && !disclosure.blocked}
                    isDisclosureSource={sequence.isDisclosure}

                    blockedMessage={
                        disclosure.blocked || sequence.clipFailed
                            ? disclosure.message
                            : undefined
                    }
                    onPlay={handleSequencePlay}
                    onPause={handleSequencePause}
                    loading={!isReadyToPlay && !content?.isBundle}
                    onUpdateExpiredUrl={() => {
                        setIsReady(false);
                        setIsReadyToPlay(true);
                    }}
                    onError={() => {
                        setIsReadyToPlay(true);
                        sequence.onError();
                    }}
                />
            </PlayerContainerGrid>

            {!content?.isBundle && (
                <BodyPaddingGrid
                    item
                    xs={12}
                    display={"flex"}
                    alignItems={"center"}
                    justifyContent={"center"}
                >
                    <PlayerActionBtns
                        isLoop={isLoop}
                        handleChangeSeek={handleChangeSeek}
                        onClickLoop={() => setLoop(!isLoop)}
                    />
                </BodyPaddingGrid>
            )}
            {content?.isBundle && (
                <PlayButtonWrapper
                    style={{ paddingRight: "10px", paddingLeft: "10px" }}
                >
                    <AppButton
                        onClick={() => playClick()}
                        buttonVariant="grey-outlined"
                        rounded
                    >
                        PLAY
                    </AppButton>
                </PlayButtonWrapper>
            )}
            <BodyPaddingGrid item xs={12}>
                <DateTypography variant="body2">
                    {moment.unix(+content?.release_date).format("LL")}
                </DateTypography>
            </BodyPaddingGrid>
            <Grid item xs={12}>
                <Grey800Typography
                    variant="body2"
                    sx={{ paddingRight: "10px", paddingLeft: "10px" }}
                >
                    {content?.description}
                </Grey800Typography>
            </Grid>
        </RootDiv>
    );
};
const useQueryParams = () => {
    const location = useLocation();

    return useMemo(() => {
        const parsedQueryString = qs.parse(location.search, {
            ignoreQueryPrefix: true,
        });

        const { scrollTo = "0" } = parsedQueryString;

        return {
            scrollTo: scrollTo,
        };
    }, [location?.search]);
};

const useDescription = (type: string, nid: string) => {
    const { audioDetails } = useGlobalAudioDetails(nid);
    const { videoDetails } = useGlobalVideoDetails(nid);

    return {
        description:
            type === "audio"
                ? audioDetails?.description
                : videoDetails?.description,
    };
};
